import { useRef } from 'react';
import { Button, Card, Col, Empty, Grid, Row, Space, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { TicketDetail } from '../support/TicketDetail';
import { TicketList } from '../support/TicketList';
import { AGENCY_SUPPORT, CATEGORY_LABEL, useSupportSummary, type SupportCategory } from '../support/api';
import { PortalPage } from './AgencyPortalPage';

const ALL_CATEGORIES = Object.keys(CATEGORY_LABEL) as SupportCategory[];

/**
 * `/agency-portal/support`: support requests from this agency's own users and
 * listeners. The agency can read, reply and close; the Dude team still sees and
 * can work every request from its own inbox, and its internal notes never
 * show here. The open request lives in `?ticket=<id>`.
 */
export function AgencySupportPage() {
  const qc = useQueryClient();
  const screens = Grid.useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('ticket');
  const detailRef = useRef<HTMLDivElement>(null);
  const summary = useSupportSummary({ refetchInterval: 20_000, scope: AGENCY_SUPPORT });
  const s = summary.data;

  function select(id: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('ticket', id);
        return next;
      },
      { replace: true },
    );
    if (!screens.lg) detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <PortalPage
      title="Support"
      extra={
        <Space wrap>
          {s && (
            <>
              <Tag color={s.open > 0 ? 'error' : 'default'}>{s.open} open</Tag>
              <Tag color={s.waitingUser > 0 ? 'processing' : 'default'}>{s.waitingUser} waiting for user</Tag>
              <Tag color={s.unread > 0 ? 'magenta' : 'default'}>{s.unread} unread</Tag>
            </>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => void qc.invalidateQueries({ queryKey: AGENCY_SUPPORT.keyPrefix })}>
            Refresh
          </Button>
        </Space>
      }
    >
      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={9} xl={8}>
          <TicketList selectedId={selectedId} onSelect={select} summary={s} categories={ALL_CATEGORIES} scope={AGENCY_SUPPORT} />
        </Col>
        <Col xs={24} lg={15} xl={16}>
          <div ref={detailRef} style={{ scrollMarginTop: 16 }}>
            {selectedId ? (
              <TicketDetail key={selectedId} id={selectedId} scope={AGENCY_SUPPORT} />
            ) : (
              <Card>
                <Empty style={{ padding: '48px 0' }} description="Select a request to read and reply" />
              </Card>
            )}
          </div>
        </Col>
      </Row>
    </PortalPage>
  );
}
